import * as React from "react";

export type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "button"
  | "caption"
  | "overline";

export type TypographyColor =
  | "initial"
  | "inherit"
  | "primary"
  | "secondary"
  | "textPrimary"
  | "textSecondary"
  | "error";

export type TypographyAlign =
  | "inherit"
  | "left"
  | "center"
  | "right"
  | "justify";

export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  align?: TypographyAlign;
  component?: React.ElementType;
  gutterBottom?: boolean;
  noWrap?: boolean;
  paragraph?: boolean;
}

// Map Material UI variants to HTML elements
const variantMap: Record<TypographyVariant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  button: "span",
  caption: "span",
  overline: "span",
};

// Map Material UI colors to CSS classes or styles
const getColorStyle = (color?: TypographyColor): React.CSSProperties => {
  switch (color) {
    case "primary":
      return { color: "var(--kendo-color-primary)" };
    case "secondary":
      return { color: "var(--kendo-color-secondary)" };
    case "error":
      return { color: "var(--kendo-color-error)" };
    case "textPrimary":
      return { color: "var(--tlx-text-primary)" };
    case "textSecondary":
      return { color: "var(--tlx-text-secondary)" };
    case "inherit":
      return { color: "inherit" };
    case "initial":
    default:
      return {};
  }
};

// Map Material UI variants to font sizes/styles
const getVariantStyles = (variant: TypographyVariant): React.CSSProperties => {
  const styles: Record<TypographyVariant, React.CSSProperties> = {
    h1: { fontSize: "2.5rem", fontWeight: 500, lineHeight: 1.2, margin: 0 },
    h2: { fontSize: "2rem", fontWeight: 500, lineHeight: 1.3, margin: 0 },
    h3: { fontSize: "1.75rem", fontWeight: 500, lineHeight: 1.4, margin: 0 },
    h4: { fontSize: "1.5rem", fontWeight: 500, lineHeight: 1.4, margin: 0 },
    h5: { fontSize: "1.25rem", fontWeight: 500, lineHeight: 1.5, margin: 0 },
    h6: { fontSize: "1rem", fontWeight: 500, lineHeight: 1.6, margin: 0 },
    subtitle1: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.75,
      margin: 0,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.57,
      margin: 0,
    },
    body1: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.5,
      margin: 0,
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.43,
      margin: 0,
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.75,
      textTransform: "uppercase",
      margin: 0,
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.66,
      margin: 0,
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 2.66,
      textTransform: "uppercase",
      margin: 0,
    },
  };
  return styles[variant];
};

export const Typography: React.FC<TypographyProps> = ({
  variant = "body1",
  color,
  align,
  component,
  gutterBottom = false,
  noWrap = false,
  paragraph = false,
  children,
  style,
  className,
  ...props
}) => {
  // Determine the HTML element to use
  const Component = component || (paragraph ? "p" : variantMap[variant]) || "p";

  // Combine styles
  const combinedStyle: React.CSSProperties = {
    ...getVariantStyles(variant),
    ...getColorStyle(color),
    ...(align && align !== "inherit" ? { textAlign: align } : {}),
    ...(gutterBottom ? { marginBottom: "0.35em" } : {}),
    ...(noWrap
      ? {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }
      : {}),
    ...style,
  };

  return (
    <Component className={className} style={combinedStyle} {...props}>
      {children}
    </Component>
  );
};

export default Typography;
